import React, { useCallback, useReducer, useState } from 'react';
import { Box, TextV2 } from '@withjoy/joykit';
import { AdminSidebarComponentProps } from '@website-editor/customComponents/CustomJsonComponent/JsonComponent.types';
import { useGoToReorderView } from '@website-editor/customComponents/CustomJsonComponent/hooks/useJsonComponentNavigation';
import { GeneralScheduleData } from './GeneralScheduleGuest';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { useJsonContainerTools } from '@website-editor/customComponents/CustomJsonComponent/hooks/useJsonBlobStorageUtils';
import { DayFilter } from './DayFilter';
import ScheduleEditor from '@shared/components/ScheduleEditor';
import { useGeneralEventScheduleGuest } from '../hooks/useGeneralEventScheduleGuest';
import { ConfirmationDialog, ConfirmationDialogMode } from './ConfirmationDialog';
import { useDeleteScheduleItem } from '@shared/components/ScheduleEditor/hooks/useDeleteScheduleItem';
import { PageFabArea } from '@website-editor/components/PageFabArea';
import { BottomActionsWrapper } from '@shared/components/ScheduleEditor/components/BottomActionsWrapper';
import { useTranslation } from '@shared/core';

const generalScheduleReducer = (state: GeneralScheduleData, action: { type: string; payload: Partial<GeneralScheduleData> }) => {
  switch (action.type) {
    case 'UPDATE':
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

type SchedulePageMode = 'view' | 'edit' | 'add';

export const GeneralScheduleEdit = ({ data, eventHandle }: AdminSidebarComponentProps) => {
  const { goToReorderView } = useGoToReorderView();
  const initialData = data.content as GeneralScheduleData;
  const [state, dispatch] = useReducer(generalScheduleReducer, initialData);
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { saveJsonData } = useJsonContainerTools(eventHandle);
  const { schedule, refetch } = useGeneralEventScheduleGuest(data.eventId);
  const { tq } = useTranslation('generalEventScheduleEditor');

  const [mode, setMode] = useState<SchedulePageMode>('view');
  const [editId, setEditId] = useState<string | null>(null);
  const [confirmationType, setConfirmationType] = useState<ConfirmationDialogMode | null>(null);

  const { deleteScheduleItem } = useDeleteScheduleItem({ refetch: ['GeneralScheduleComponentScheduleForEvent'] });

  const handleUpdate = useCallback(
    async (key: keyof GeneralScheduleData, value: GeneralScheduleData[keyof GeneralScheduleData]) => {
      dispatch({ type: 'UPDATE', payload: { [key]: value } });
      await saveJsonData({
        id: data.id,
        content: { ...state, [key]: value },
      });
      updateWebsitePreview();
    },
    [state, data.id, saveJsonData, updateWebsitePreview],
  );

  const renderContent = useCallback(() => {
    switch (mode) {
      case 'view':
        return (
          <DayFilter
            schedule={schedule}
            excludedDays={state.exclusiveDayFilter ?? []}
            setExcludedDays={value => handleUpdate('exclusiveDayFilter', value)}
            onEditEvent={id => {
              setMode('edit');
              setEditId(id);
            }}
            onAddEvent={() => {
              setMode('add');
            }}
            setConfirmationType={setConfirmationType}
            setDeleteId={setEditId}
          />
        );
      case 'edit':
      case 'add':
        return (
          <ScheduleEditor
            mode={mode}
            eventId={data.eventId}
            scheduleItemId={editId!}
            onSuccess={async () => {
              await refetch();
              updateWebsitePreview();
              setMode('view');
            }}
            onError={() => console.error('Failed to edit schedule')}
            onCancel={async () => {
              await refetch();
              updateWebsitePreview();
              setMode('view');
            }}
            refetch={['GeneralScheduleComponentScheduleForEvent']}
          />
        );
      default:
        return null;
    }
  }, [mode, schedule, state, handleUpdate, data.eventId, editId, refetch, updateWebsitePreview]);

  return (
    <Box padding="2rem">
      <TextV2
        typographyVariant="hed5"
        marginBottom="1rem"
      >
        {tq`Event Schedule`}
      </TextV2>
      {renderContent()}

      <ConfirmationDialog
        mode={confirmationType}
        onDoCancel={async () => {
          if (confirmationType === 'delete') {
            if (editId) {
              await deleteScheduleItem(editId);
              updateWebsitePreview();
            }
          }
          setConfirmationType(null);
        }}
        setConfirmationType={setConfirmationType}
      />
      <BottomActionsWrapper>
        {mode === 'view' && (
          <PageFabArea
            addAction={{
              label: tq`Back`,
              onClick: () => goToReorderView(),
            }}
          />
        )}
      </BottomActionsWrapper>
    </Box>
  );
};
